import React, { useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import ReactECharts from "echarts-for-react";
import { Lightbulb } from "lucide-react";
import HexagonTick from "app/assets/IconComponent/HexagonTick";
import FundingIcon from "app/assets/IconComponent/FundingIcon";
import WarningHex from "app/assets/IconComponent/WarningHex";

// ── Constants ─────────────────────────────────────────────────────────────────

const PRIMARY = "#1E3A8A";
const CURRENT_YEAR = new Date().getFullYear();
const INITIAL_VISIBLE_RECORDS = 10;

const PAYMENT_STATUS = {
  paid: { color: "#22C55E", label: "Paid" },
  due: { color: "#3B82F6", label: "Due" },
  overdue: { color: "#F97316", label: "Overdue" },
} as const;

type PaymentStatus = keyof typeof PAYMENT_STATUS;

const SERIES_CONFIG = [
  {
    id: "CLIENT_PREMIUM",
    label: "Client Premium",
    color: "#1DA750",
    key: "clientPremium" as const,
    illustratedKey: "clientPremiumIllustrated" as const,
  },
  {
    id: "LENDER_PREMIUM",
    label: "Lender Premium",
    // eslint-disable-next-line
    color: "#004781",
    key: "lenderPremium" as const,
    illustratedKey: "lenderPremiumIllustrated" as const,
  },
  {
    id: "INTEREST_PAYMENT",
    label: "Interest Payment",
    // eslint-disable-next-line
    color: "#FF3D32",
    key: "clientInterestPayment" as const,
    illustratedKey: "clientInterestPaymentIllustrated" as const,
  },
  {
    id: "PLAN_FEE",
    label: "Plan Fee",
    // eslint-disable-next-line
    color: "#FF6A00",
    key: "clientPlanFee" as const,
    illustratedKey: "clientPlanFeeIllustrated" as const,
  },
  {
    id: "ORIGINATION_FEE",
    label: "Origination Fee",
    // eslint-disable-next-line
    color: PRIMARY + "33",
    key: "clientOriginationFees" as const,
    illustratedKey: null,
  },
] as const;

const SERIES_ICONS: Record<string, React.ComponentType<any>> = {
  CLIENT_PREMIUM: HexagonTick,
  LENDER_PREMIUM: FundingIcon,
  INTEREST_PAYMENT: WarningHex,
  PLAN_FEE: HexagonTick,
  ORIGINATION_FEE: HexagonTick,
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FundingChartRow {
  planYear: number;
  year: number;
  insuredAge: number;
  clientPremium: number;
  clientPlanFee: number;
  clientInterestPayment: number;
  clientOriginationFees: number;
  lenderPremium: number;
  lenderOriginationFees: number;
  totalPolicyPremium: number;
  clientPremiumIllustrated: number;
  lenderPremiumIllustrated: number;
  clientInterestPaymentIllustrated: number;
  clientPlanFeeIllustrated: number;
  trueIQIndex?: number;
}

interface TooltipState {
  row: FundingChartRow;
  x: number;
  y: number;
}

interface FundingChartProps {
  data: FundingChartRow[];
  activeFilters: Set<string>;
  isDark: boolean;
  selectedYear: number;
  loading?: boolean;
  showProjections: boolean;
  onShowProjectionsChange: (v: boolean) => void;
  isContact: boolean;
  showIndex?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (v: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);

function getPaymentStatus(
  actual: number,
  _illustrated: number,
  calendarYear: number,
): PaymentStatus {
  if (actual > 0) return "paid";
  return calendarYear < CURRENT_YEAR ? "overdue" : "due";
}

// ── Status icon per payment state ─────────────────────────────────────────────

function StatusIcon({ status, size = 14 }: { status: PaymentStatus; size?: number }) {
  const { color } = PAYMENT_STATUS[status];

  if (status === "paid") {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="7" fill={color} />
        <path
          d="M4 7L6 9.5L10 5"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (status === "overdue") {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="7" fill={color} />
        <path d="M7 4V7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="9.5" r="0.8" fill="white" />
      </svg>
    );
  }

  // due → blue hexagon with $
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7 0.5L13.2 3.75V10.25L7 13.5L0.8 10.25V3.75L7 0.5Z" fill={color} />
      <text
        x="7"
        y="9.5"
        textAnchor="middle"
        fontSize="5.5"
        fill="white"
        fontWeight="800"
        fontFamily="sans-serif"
      >
        $
      </text>
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

function FundingChart({
  data,
  activeFilters,
  isDark,
  selectedYear,
  loading,
  showProjections,
  onShowProjectionsChange,
  isContact,
  showIndex,
}: FundingChartProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [selectedBarIndex, setSelectedBarIndex] = useState<number | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoomStateRef = useRef<{ start: number; end: number } | null>(null);

  const nextYear = selectedYear + 1;

  // ── ECharts option ───────────────────────────────────────────────────────────

  const option = useMemo(() => {
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const gridColor = isDark ? "#3A3A50" : "#e2e8f0";

    // ── Shared pill styling (applied to every stacked segment) ───────────────
    // Change these three constants to adjust ALL bars at once.
    const PILL_RADIUS = 2; // corner radius (px)
    const PILL_GAP = 2; // gap between stacked segments (px)
    const PILL_BG = isDark ? "#2A2A3D" : "#ffffff"; // must match the card background

    const nextYr = selectedYear + 1;
    const lightBlue = isDark ? "rgba(96,165,250,0.35)" : "rgba(147,197,253,0.65)";

    const activeCfgs = SERIES_CONFIG.filter((s) => activeFilters.has(s.id));

    // ── Stacked data series (one per active component) ───────────────────────
    const dataSeries = activeCfgs.map((cfg) => ({
      name: cfg.label,
      type: "bar",
      stack: "total",
      barMaxWidth: 80,
      barCategoryGap: "20%",
      data: data.map((row, rowIndex) => {
        const isCompleted = row.year <= selectedYear;
        const isNextYr = row.year === nextYr;
        const isProjection = row.year > selectedYear;
        const isSelected = rowIndex === selectedBarIndex;

        if (isProjection && !showProjections) {
          return { value: 0, itemStyle: { color: "transparent" } };
        }

        const total = activeCfgs.reduce((sum, cfgData) => {
          const val =
            isProjection && cfgData.illustratedKey
              ? row[cfgData.illustratedKey] ?? 0
              : row[cfgData.key] ?? 0;
          return sum + val;
        }, 0);

        if (isProjection) {
          return {
            value: total,
            itemStyle: {
              color: isDark ? "rgba(96,165,250,0.22)" : "rgba(147,197,253,0.45)",
              borderColor: isDark ? "rgba(96,165,250,0.7)" : "#3B82F6",
              borderType: "dashed",
              borderWidth: 1.5,
            },
          };
        }

        // ── Selected bar: reveal per-component SERIES_CONFIG colors ─────────
        if (isSelected) {
          const val =
            isProjection && cfg.illustratedKey ? row[cfg.illustratedKey] ?? 0 : row[cfg.key] ?? 0;
          return {
            value: val,
            itemStyle: {
              color: isProjection ? `${cfg.color}99` : cfg.color,
              borderRadius: PILL_RADIUS,
              borderWidth: PILL_GAP,
              borderColor: PILL_BG,
              borderType: "solid",
            },
          };
        }

        // ── Completed (not selected): aggregate color, same pill shape ───────
        if (isCompleted) {
          return {
            value: row[cfg.key] ?? 0,
            itemStyle: {
              color: PRIMARY,
              borderRadius: PILL_RADIUS,
              borderWidth: PILL_GAP,
              borderColor: PILL_BG,
              borderType: "solid",
            },
          };
        }

        const illustratedVal = cfg.illustratedKey
          ? row[cfg.illustratedKey] ?? 0
          : row[cfg.key] ?? 0;

        // Immediate next year: payment-status colors, same pill shape
        if (isNextYr && cfg.illustratedKey) {
          const actual = row[cfg.key] ?? 0;
          const status = getPaymentStatus(actual, illustratedVal, row.planYear);
          const c = PAYMENT_STATUS[status].color;
          return {
            value: illustratedVal,
            itemStyle: {
              color: `${c}33`,
              borderColor: PILL_BG,
              borderType: "solid",
              borderWidth: PILL_GAP,
              borderRadius: PILL_RADIUS,
            },
          };
        }

        // Future years (2+ ahead): uniform light blue, same pill shape
        return {
          value: illustratedVal,
          itemStyle: {
            color: lightBlue,
            borderRadius: PILL_RADIUS,
            borderWidth: PILL_GAP,
            borderColor: PILL_BG,
            borderType: "solid",
          },
        };
      }),
    }));

    // ── Outline series: dashed border for unselected future projection bars ──
    const outlineSeries = {
      name: "__outline__",
      type: "bar",
      barMaxWidth: 80,
      barCategoryGap: "20%",
      barGap: "-100%",
      silent: true,
      data: data.map((row, rowIndex) => {
        const isFuture = row.year > nextYr;
        const isSelected = rowIndex === selectedBarIndex;
        if (!isFuture || !showProjections || isSelected) {
          return { value: 0, itemStyle: { color: "transparent", borderWidth: 0 } };
        }
        const total = activeCfgs.reduce((sum, cfg) => {
          const v = cfg.illustratedKey ? row[cfg.illustratedKey] ?? 0 : row[cfg.key] ?? 0;
          return sum + v;
        }, 0);
        return {
          value: total,
          itemStyle: {
            color: "transparent",
            borderColor: isDark ? "rgba(96,165,250,0.7)" : "#3B82F6",
            borderType: "dashed",
            borderWidth: 1.5,
          },
        };
      }),
    };

    // ── TrueIQ index label series: badge on top of each bar when showIndex=true ─
    const iconColor = isDark ? "#ffffff" : "#1E3A8A";
    const trueIqSvgStr = [
      `<svg width="14" height="11" viewBox="0 0 54 49" xmlns="http://www.w3.org/2000/svg">`,
      `<path d="M31.6871 44.3324C30.7013 42.9642 29.1175 42.1548 27.4314 42.1548H18.4944C17.0884 42.1422 15.7693 41.379 15.0523 40.1663L6.87847 26.2902C6.13208 25.0257 6.13208 23.4545 6.87847 22.19L13.5974 10.7841H6.26091L1.4311 18.9818C-0.478985 22.225 -0.478985 26.2552 1.4311 29.4984L9.60636 43.3759C11.431 46.4707 14.7821 48.4186 18.367 48.4732C18.4426 48.476 18.5 48.476 18.5322 48.476H34.6727L31.6857 44.3324H31.6871ZM52.2387 18.9804L44.0634 5.10289C42.2094 1.95489 38.7897 0 35.1376 0H26.4414L22.6941 6.32119H35.1376C36.5618 6.32119 37.8949 7.08438 38.6189 8.31109L46.7941 22.1872C47.5391 23.4517 47.5391 25.0229 46.7941 26.2874L40.9365 35.2259L44.611 41.4448L52.2401 29.4956C54.1516 26.2524 54.1516 22.2222 52.2401 18.979L52.2387 18.9804ZM9.60776 5.10289L8.88938 6.32119H16.216L19.92 0H18.535C14.8829 0 11.4632 1.95489 9.60776 5.10289Z" fill="${iconColor}"/>`,
      `<path d="M36.7396 22.8095L36.4133 22.2451L36.6556 22.6554C36.685 22.7058 36.7144 22.7576 36.7396 22.8095Z" fill="${iconColor}"/>`,
      `<path d="M48.5281 46.5617H44.2304C43.1298 46.5617 42.0921 46.0267 41.4535 45.1305L33.5892 34.1055C32.5081 32.5904 30.752 31.6857 28.891 31.6843C26.6532 31.6843 23.4128 31.6843 23.2223 31.6843C22.7238 31.6773 22.2575 31.4071 22.004 30.9757L18.4654 24.9696C18.1993 24.5187 18.1993 23.96 18.464 23.5105L22.004 17.5015C22.2617 17.0646 22.7364 16.793 23.242 16.793H30.4314C30.9369 16.793 31.4116 17.0646 31.6693 17.5015L34.9629 23.0932L48.5281 46.5631V46.5617Z" fill="${iconColor}"/>`,
      `</svg>`,
    ].join("");
    const trueIqDataUrl = `data:image/svg+xml,${encodeURIComponent(trueIqSvgStr)}`;

    const indexSeries = {
      name: "__index__",
      type: "bar",
      barMaxWidth: 80,
      barCategoryGap: "20%",
      barGap: "-100%",
      silent: true,
      data: data.map((row) => {
        const isProjection = row.year > selectedYear;
        if (isProjection && !showProjections) {
          return {
            value: 0,
            label: { show: false },
            itemStyle: { color: "transparent", borderWidth: 0 },
          };
        }
        const total = activeCfgs.reduce((sum, cfg) => {
          const v =
            isProjection && cfg.illustratedKey ? row[cfg.illustratedKey] ?? 0 : row[cfg.key] ?? 0;
          return sum + v;
        }, 0);
        return {
          value: total,
          label: { show: showIndex && row.trueIQIndex != null },
          itemStyle: { color: "transparent", borderWidth: 0 },
        };
      }),
      label: {
        show: showIndex,
        position: "top" as const,
        distance: 4,
        formatter: (params: any) => {
          const row = data[params.dataIndex];
          const val = row?.trueIQIndex;
          if (val == null) return "";
          return `{icon| }{val|${val.toFixed(1)}}`;
        },
        rich: {
          icon: {
            width: 14,
            height: 11,
            backgroundColor: { image: trueIqDataUrl },
            verticalAlign: "middle",
          },
          val: {
            fontSize: 10,
            color: textColor,
            fontWeight: "bold",
            verticalAlign: "middle",
          },
        },
      },
    };

    const useInitialWindow = !zoomStateRef.current;
    const initialEndIndex = useInitialWindow
      ? Math.min(INITIAL_VISIBLE_RECORDS - 1, Math.max(data.length - 1, 0))
      : undefined;

    return {
      animation: true,
      tooltip: { show: false },
      legend: { show: false },
      grid: { left: 56, right: 12, top: 12, bottom: 56 },
      dataZoom: [
        {
          type: "slider",
          show: true,
          start: useInitialWindow ? undefined : zoomStateRef.current?.start ?? 0,
          end: useInitialWindow
            ? undefined
            : zoomStateRef.current?.end ??
              Math.min(100, (INITIAL_VISIBLE_RECORDS / (data.length || 1)) * 100),
          startValue: useInitialWindow ? 0 : undefined,
          endValue: useInitialWindow ? initialEndIndex : undefined,
          minValueSpan: 1,
          maxValueSpan: data.length || 1,
          height: 24,
          bottom: 4,
          borderColor: "transparent",
          fillerColor: isDark ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
          handleStyle: {
            color: isDark ? "#ffffff" : "#334155",
            borderColor: isDark ? "#4A4A60" : "#E5E7EB",
          },
          textStyle: { color: textColor, fontSize: 10 },
          showDetail: true,
          showDataShadow: true,
          realtime: true,
          filterMode: "filter",
          xAxisIndex: 0,
        },
      ],
      xAxis: {
        type: "category",
        data: data.map((r) => `Age ${r.insuredAge}`),
        axisLine: { lineStyle: { color: gridColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 11 },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: (v: number) => (v >= 1000 ? `$${v / 1000}K` : `$${v}`),
        },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
      },
      series: [...dataSeries, outlineSeries, indexSeries],
    };
  }, [data, activeFilters, isDark, selectedYear, showProjections, selectedBarIndex, showIndex]);

  // ── Mouse event handlers ─────────────────────────────────────────────────────

  const cancelHide = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => setTooltip(null), 160);
  };

  const onBarClick = (params: any) => {
    if (params.seriesName === "__outline__" || params.seriesName === "__index__") return;
    const idx = params.dataIndex;
    setSelectedBarIndex((prev) => (prev === idx ? null : idx));
  };

  const onMouseOver = (params: any) => {
    cancelHide();
    if (params.seriesName === "__outline__" || params.seriesName === "__index__") return;
    const row = data[params.dataIndex];
    if (!row) return;
    const evt = params.event?.event as MouseEvent | undefined;
    if (!evt) return;
    setTooltip({ row, x: evt.clientX, y: evt.clientY });
  };

  // ── Tooltip state ────────────────────────────────────────────────────────────

  const tooltipRow = tooltip?.row ?? null;
  const isNextYear = tooltipRow?.year === nextYear;

  const hasUnpaid =
    isNextYear && tooltipRow
      ? SERIES_CONFIG.some(
          (s) =>
            activeFilters.has(s.id) &&
            s.illustratedKey !== null &&
            tooltipRow[s.key] === 0 &&
            tooltipRow[s.illustratedKey!] > 0,
        )
      : false;

  // ── Theme tokens ─────────────────────────────────────────────────────────────

  const borderColor = isDark ? "#3A3A50" : "#E5E7EB";
  const panelBg = isDark ? "#1E2030" : "#FFFFFF";
  const textMain = isDark ? "#F1F5F9" : "#1E293B";
  const textSub = isDark ? "#94A3B8" : "#64748B";

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Projections toggle ────────────────────────────────────────────── */}
      <div className="flex justify-end mb-1">
        <label
          className="flex items-center gap-1.5 cursor-pointer select-none"
          onClick={() => onShowProjectionsChange(!showProjections)}
        >
          <div
            className="w-3.5 h-3.5 rounded-sm border-2 flex items-center justify-center flex-shrink-0 transition-colors"
            style={{
              borderColor: showProjections ? PRIMARY : borderColor,
              backgroundColor: showProjections ? PRIMARY : "transparent",
            }}
          >
            {showProjections && (
              <svg width="8" height="6" viewBox="0 0 10 8" fill="none">
                <path
                  d="M1 4L3.5 6.5L9 1"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <Lightbulb
            size={11}
            fill={showProjections ? `${PRIMARY}30` : "none"}
            stroke={showProjections ? PRIMARY : textSub}
          />
          <span
            className="text-[11px] font-medium"
            style={{ color: showProjections ? PRIMARY : textSub }}
          >
            Projections
          </span>
        </label>
      </div>

      {/* ── Chart ─────────────────────────────────────────────────────────── */}
      <ReactECharts
        option={option}
        style={{ height: 300, cursor: "default" }}
        notMerge
        lazyUpdate
        showLoading={loading}
        loadingOption={{
          text: "",
          color: PRIMARY,
          maskColor: isDark ? "rgba(30,32,48,0.8)" : "rgba(255,255,255,0.8)",
        }}
        onEvents={{
          click: onBarClick,
          mouseover: onMouseOver,
          mouseout: scheduleHide,
          dataZoom: (params: any) => {
            zoomStateRef.current = { start: params.start, end: params.end };
          },
        }}
      />

      {tooltip &&
        tooltipRow &&
        createPortal(
          <div
            className="fixed rounded-xl border shadow-2xl"
            style={{
              left: Math.min(tooltip.x + 14, window.innerWidth - 260),
              top: Math.max(tooltip.y - 24, 8),
              background: panelBg,
              borderColor,
              minWidth: 230,
              zIndex: 99999,
            }}
            onMouseEnter={cancelHide}
            onMouseLeave={() => setTooltip(null)}
          >
            {/* Items */}
            <div className="px-3 pt-2 pb-2.5 flex flex-col gap-2">
              {SERIES_CONFIG.map((cfg) => {
                if (!activeFilters.has(cfg.id)) return null;

                const actual = tooltipRow[cfg.key];
                const illustrated = cfg.illustratedKey ? tooltipRow[cfg.illustratedKey] : actual;

                let statusNode: React.ReactNode;
                let labelColor = textSub;
                let rowBg: string | undefined;

                if (isNextYear && cfg.illustratedKey) {
                  const status = getPaymentStatus(actual, illustrated, tooltipRow.planYear);
                  labelColor = PAYMENT_STATUS[status].color;
                  rowBg = `${PAYMENT_STATUS[status].color}20`;
                  statusNode = <StatusIcon status={status} size={15} />;
                } else {
                  const Icon = SERIES_ICONS[cfg.id];
                  statusNode = (
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: cfg.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon fill="#fff" width={10} height={10} />
                    </div>
                  );
                }

                const displayVal = isNextYear && cfg.illustratedKey ? illustrated : actual;

                return (
                  <div
                    key={cfg.id}
                    className="flex items-center justify-between gap-3"
                    style={
                      rowBg
                        ? {
                            background: rowBg,
                            borderRadius: 6,
                            padding: "3px 6px",
                            margin: "0 -6px",
                          }
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      {statusNode}
                      <span
                        className="text-[12px] font-medium truncate"
                        style={{ color: labelColor }}
                      >
                        {cfg.label}
                      </span>
                    </div>
                    <span
                      className="text-[11px] font-semibold tabular-nums shrink-0"
                      style={{ color: textMain }}
                    >
                      {fmt(displayVal)}
                    </span>
                  </div>
                );
              })}

              <div className="border-t pt-1.5 mt-0.5" style={{ borderColor }}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold" style={{ color: textSub }}>
                    Total Premium
                  </span>
                  <span className="text-[12px] font-bold" style={{ color: PRIMARY }}>
                    {fmt(tooltipRow.totalPolicyPremium)}
                  </span>
                </div>
              </div>

              {isNextYear && hasUnpaid && (
                <button
                  type="button"
                  disabled={!isContact}
                  className="w-full mt-0.5 py-1.5 rounded-lg text-[12px] font-bold transition-all"
                  style={{
                    background: "transparent",
                    color: isContact ? "#3B82F6" : textSub,
                    border: `1.5px solid ${isContact ? "#3B82F6" : borderColor}`,
                    cursor: isContact ? "pointer" : "not-allowed",
                    opacity: isContact ? 1 : 0.5,
                  }}
                >
                  Pay Now
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default FundingChart;
